import CommentIcon from '@mui/icons-material/Comment';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { useAuth } from '@verantos-dev/core-auth';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useAnnotationThread } from './useAnnotations';

interface AnnotationThreadProps {
  targetId: string;
  currentBlockHash: string;
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function AnnotationThread({ targetId, currentBlockHash }: AnnotationThreadProps) {
  const [open, setOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const {
    user: { userId },
  } = useAuth();

  const { thread, isLoading, addComment, removeComment, setResolved, dismissStale } =
    useAnnotationThread(targetId, !open);

  const comments = thread?.comments?.filter((c) => !c.deleted) ?? [];
  const metadata = thread?.metadata;
  const commentCount = comments.length;
  const isResolved = metadata?.resolved ?? false;

  const hasStaleContent =
    comments.some((c) => c.contentHash && c.contentHash !== currentBlockHash) &&
    !metadata?.stalenessReviewedAt;

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    await addComment(newComment.trim(), currentBlockHash);
    setNewComment('');
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <IconButton size="small" onClick={() => setOpen(!open)}>
        <Badge badgeContent={commentCount || undefined} color="primary">
          <CommentIcon fontSize="small" color={commentCount > 0 ? 'primary' : 'disabled'} />
        </Badge>
      </IconButton>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            ml: 1,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: `${borderRadius.md}px`,
            minWidth: 320,
            maxWidth: 480,
            bgcolor: 'background.paper',
          }}
        >
          {/* Resolve toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Thread: {targetId}
            </Typography>
            <Chip
              label={isResolved ? 'Resolved' : 'Open'}
              size="small"
              color={isResolved ? 'success' : 'default'}
              onClick={() => setResolved(!isResolved)}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          {/* Staleness banner */}
          {hasStaleContent && (
            <Alert severity="warning" sx={{ mb: 1 }} onClose={dismissStale}>
              Content updated — review comments
            </Alert>
          )}

          {/* Comments */}
          {isLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          )}
          {comments.map((comment) => (
            <Box
              key={comment.annotationId}
              sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: `${borderRadius.md}px` }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" fontWeight={600}>
                  {comment.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatRelativeTime(comment.createdAt)}
                </Typography>
              </Box>
              <Typography variant="body2">{comment.text}</Typography>
              {comment.userId === userId && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => removeComment(comment.annotationId)}
                  sx={{ mt: 0.5, minWidth: 'auto', p: 0, fontSize: '0.7rem' }}
                >
                  Delete
                </Button>
              )}
            </Box>
          ))}

          {/* New comment input */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
              size="small"
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              fullWidth
              multiline
              maxRows={3}
            />
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={!newComment.trim()}
            >
              Post
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
